// The company surface — blog posts + product updates (the daily dispatch).
// One shape carries both kinds; `data` preserves the dispatch anatomy so the
// reader can render the rich statistics treatment natively.

export interface CompanyMedia {
  type: 'image' | 'video' | 'youtube'
  /** image/video: the asset url. youtube: unused (see videoId). */
  src: string
  /** youtube video id — the embed + thumbnail derive from it */
  videoId?: string
  /** poster/thumbnail frame (videos, and an optional local youtube thumbnail) */
  poster?: string
  /** the media's primary color — the expanded viewer tints its ground with it */
  tone?: string
  alt?: string
  /**
   * a generated line of context — what this media is and why it matters, in
   * innernet's own voice. every piece of media carries one; shown under the
   * media in the expanded viewer. authored by the model at publish time.
   */
  context?: string
}

export type DispatchItem = string | { title: string; note?: string }

/**
 * One part of the product that grew — the dispatch is divided by WHERE innernet
 * expanded, not by ship status. `line` is the zone's own one-sentence summary;
 * `items` are how much detail that zone earned today (none, one, a few).
 */
export interface DispatchZone {
  zone: string
  line?: string
  items?: DispatchItem[]
}

/** the dispatch.json anatomy (see .claude/skills/product-dispatch/references/anatomy.md) */
export interface DispatchData {
  date?: string
  edition?: string
  headline?: string
  dek?: string
  stats?: { value: string | number; label: string }[]
  /** the day by expansion zone — the anatomy since 2026-09-23. */
  zones?: DispatchZone[]
  /** one or two quiet lines: the small fixes, said in passing. */
  also?: string
  /** the legacy status sections — older editions, still rendered. */
  shipped?: DispatchItem[]
  fixed?: DispatchItem[]
  in_flight?: DispatchItem[]
  notes?: DispatchItem[]
  thread?: string
  provenance?: string
  signoff?: string
  signrole?: string
}

export type CompanyKind = 'blog' | 'update' | 'inside' | 'report'

/** the kinds anyone may read. Every other kind (the dispatch archive, the
 *  reports) is founders' — an allow-list, so a new private kind is private
 *  without anyone remembering to say so. */
export const PUBLIC_KINDS: readonly CompanyKind[] = ['blog', 'update']

export function isPublicKind(kind: string): boolean {
  return (PUBLIC_KINDS as readonly string[]).includes(kind)
}

/** Keep-a-Changelog groups — the granularity lives in the line items, not the
 *  version axis, so a release reads as one coherent story. */
export type ChangeGroup = 'Added' | 'Changed' | 'Fixed' | 'Removed' | 'Security'

/** the `data` anatomy for a versioned `update` (a release / changelog entry) —
 *  the twin of DispatchData. `version` mirrors the column for the reader. */
export interface ReleaseData {
  version: string
  headline?: string
  sections: {
    label: ChangeGroup
    items: { title: string; note?: string; area?: string }[]
  }[]
}

/** one thing that was said out loud, on a platform, by us or about us. */
export interface LetterSaid {
  /** 'x' · 'instagram' · … — resolves to a real mark via BrandIcon */
  platform: string
  url: string
  /** the account that said it, e.g. '@yourInnernet' */
  handle: string | null
  /** their words, as posted */
  quote: string | null
  /** one line of innernet context — why this is in the letter */
  note: string | null
  /** ours, or someone else's */
  ours: boolean
  when: string | null
}

/**
 * the `data` anatomy for the weekly public letter — the third native shape,
 * beside DispatchData (the daily dispatch) and ReleaseData (a release).
 *
 * The letter used to be markdown only, which meant its only way to show a
 * feature was an `![image]` of a generated card carrying the feature's NAME —
 * a picture of a title. Structure lets the reader render each part in the
 * house language instead: tick rows for what you can do, a quiet quote for
 * what was said, no pictures of words.
 */
export interface LetterData {
  /** the discriminator — this `data` is a weekly letter */
  letter: 'week'
  /** the week it covers, e.g. '30 july' */
  week: string
  /** one or two lines: what this week was */
  opener?: string
  /** what a reader can newly do */
  can_do?: { name: string; summary: string; href?: string }[]
  /** what got quieter, faster, safer — the week's quality-of-life pass */
  smoothed?: DispatchItem[]
  /** versioned releases published this week */
  shipped?: { title: string; dek?: string; href: string; version?: string }[]
  /** letters we published this week */
  wrote?: { title: string; dek?: string; href: string }[]
  /** what was said out loud — ours first */
  said?: LetterSaid[]
}

/**
 * the `data` anatomy for a report (kind='report') — a founders' reading of the
 * whole company. The words live in `body_md`; `data` carries what lets the
 * next reading stand beside this one: the date it was read AS OF, the numbers
 * it stands on (innernet_report_metrics, migration 139), and the stats strip.
 */
export interface ReportData {
  /** the discriminator — this `data` is a report */
  report: 'state' | (string & {})
  /** the instant the numbers were read as of (ISO) */
  as_of: string
  /** days since launch (2026-07-08) on the founders' calendar */
  days_since_launch?: number
  /** the strip at the top: a few numbers, each with its label */
  stats?: { value: string | number; label: string }[]
  /** the raw reading the body was written from — what the next report diffs against */
  metrics?: Record<string, unknown>
  /** who or what wrote the reading: the ritual's numbers, or a founder's session */
  drafted_by?: 'ritual' | 'session' | (string & {})
}

export interface CompanyPost {
  id: string
  slug: string
  kind: CompanyKind
  title: string
  dek: string | null
  body_md: string | null
  /** the dispatch anatomy (inside), the release changelog (update), or the
   *  weekly letter (blog · this week at innernet). */
  data: DispatchData | ReleaseData | LetterData | ReportData | null
  media: CompanyMedia[]
  author: string
  category: string
  /** a finer facet within a kind — e.g. `product-dispatch` under `inside`. */
  subcategory: string | null
  /** the marketing version on a release (`kind='update'`), e.g. "1.0". */
  version: string | null
  pinned: boolean
  hue: number | null
  status: 'draft' | 'published'
  published_at: string | null
  created_at: string
  updated_at: string
}
